import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { cn } from "@lib/cn";
import {
	IconArrowsRightLeft,
	IconColorPicker,
	IconGripVertical,
	IconMinus,
	IconPlus,
	IconRotateClockwise2,
} from "@tabler/icons-react";
import {
	type CSSProperties,
	type Dispatch,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import type { RgbaColor } from "react-colorful";
import Input from "../../ui/input";
import Tooltip from "../../ui/tooltip";
import type {
	GradientState,
	GradientStateAction,
} from "../hooks/use-gradient-state";
import {
	hexToRgba,
	isCssVar,
	normalizeHexColor,
	resolveCssVar,
	rgbaToHex,
} from "../lib/rgba";
import { MemoizedColorPicker } from "../solid-editor";
import { StopsTrack } from "./stops-track";
import { TypeSelector } from "./type-selector";

export type GradientEditorProps = {
	state: GradientState;
	dispatch: Dispatch<GradientStateAction>;
	css: string;
	canAddStop: boolean;
	canRemoveStop: boolean;
	/** Theme color swatches to show in the stop color picker. */
	themeColors?: { key: string; tooltip: string }[];
	/** Custom solid colors to show in the stop color picker. */
	customSolidColors?: string[];
	/** Called when user clicks "Add gradient" to save the gradient. */
	onAddGradient?: () => void;
	/** Called when user clicks "Go back" to exit the gradient editor. */
	onGoBack?: () => void;
	/** When true, the save button says "Save changes" instead of "Add gradient" / "Use color". */
	isEditing?: boolean;
};

const HEX_RE = /^#?([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;

const CHECKER_SWATCH_STYLE: CSSProperties = {
	backgroundImage:
		"linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
	backgroundSize: "6px 6px",
	backgroundPosition: "0 0, 0 3px, 3px -3px, -3px 0",
};

const DEFAULT_THEME_COLORS = [
	{ key: "var(--theme-color-1)", tooltip: "Primary" },
	{ key: "var(--theme-color-2)", tooltip: "Secondary" },
	{ key: "var(--theme-color-3)", tooltip: "Accent #1" },
	{ key: "var(--theme-color-4)", tooltip: "Accent #2" },
	{ key: "var(--background-main-color)", tooltip: "Background #1" },
	{ key: "var(--background-secondary-color)", tooltip: "Background #2" },
];

export const GradientEditor = ({
	state,
	dispatch,
	css: _css,
	canAddStop,
	canRemoveStop,
	themeColors = DEFAULT_THEME_COLORS,
	customSolidColors = [],
	onAddGradient,
	onGoBack,
	isEditing,
}: GradientEditorProps) => {
	/** null = stops list view, string = editing that stop's color */
	const [editingStopId, setEditingStopId] = useState<string | null>(null);

	const [announcement, setAnnouncement] = useState("");
	const prevStopsRef = useRef(state.gradient.stops);

	// Announce stop additions / removals
	useEffect(() => {
		const prev = prevStopsRef.current;
		const next = state.gradient.stops;
		prevStopsRef.current = next;
		if (next.length > prev.length) {
			const added = next.find((s) => !prev.some((p) => p.id === s.id));
			if (added) {
				setAnnouncement(
					`Stop added at ${Math.round(added.position)}%, ${added.color}. ${next.length} stops total.`,
				);
			}
		} else if (next.length < prev.length) {
			setAnnouncement(`Stop removed. ${next.length} stops total.`);
		}
	}, [state.gradient.stops]);

	// Reverse stops
	const handleReverse = useCallback(() => {
		dispatch({ kind: "reverseStops" });
	}, [dispatch]);

	// Rotate angle by 45°
	const currentAngle =
		state.gradient.type === "linear" ? state.gradient.angle : 0;
	const handleRotate = useCallback(() => {
		const nextAngle = (Math.round(currentAngle / 45) * 45 + 45) % 360;
		dispatch({ kind: "setAngle", value: nextAngle });
	}, [dispatch, currentAngle]);

	const sortedStops = [...state.gradient.stops].sort(
		(a, b) => a.position - b.position,
	);

	// DnD sensors & handler for reordering stops
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
	);

	const handleDragEnd = useCallback(
		(e: DragEndEvent) => {
			const { active, over } = e;
			if (!over || active.id === over.id) return;
			dispatch({
				kind: "swapStopPositions",
				idA: active.id as string,
				idB: over.id as string,
			});
		},
		[dispatch],
	);

	const [animateRef] = useAutoAnimate();

	const editingStop = editingStopId
		? state.gradient.stops.find((s) => s.id === editingStopId)
		: null;

	return (
		<div ref={animateRef} data-testid="gradient-editor">
			{editingStopId && editingStop ? (
				<div key="stop-editor">
					<StopColorEditor
						stop={editingStop}
						dispatch={dispatch}
						themeColors={themeColors}
						customSolidColors={customSolidColors}
						onGoBack={() => setEditingStopId(null)}
					/>
				</div>
			) : (
				<div key="stops-list" className="tw-flex tw-flex-col tw-gap-4">
					{/* Header: type selector + reverse + rotate */}
					<div className="tw-flex tw-items-center tw-gap-2">
						<div className="tw-flex-1">
							<TypeSelector
								value={state.gradient.type}
								onChange={(value) => dispatch({ kind: "setType", value })}
							/>
						</div>
						<div className="tw-flex tw-items-center tw-gap-0.5">
							<button
								type="button"
								aria-label="Reverse stops"
								onClick={handleReverse}
								className="tw-flex tw-size-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-text-[#12033E] tw-transition-colors hover:tw-bg-[#F3F2F5]"
							>
								<IconArrowsRightLeft className="tw-size-4" />
							</button>
							{state.gradient.type === "linear" && (
								<button
									type="button"
									aria-label="Rotate 45°"
									onClick={handleRotate}
									className="tw-flex tw-size-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-text-[#12033E] tw-transition-colors hover:tw-bg-[#F3F2F5]"
								>
									<IconRotateClockwise2 className="tw-size-4" />
								</button>
							)}
						</div>
					</div>

					{/* Stops track + gradient preview */}
					<div className="tw-flex tw-items-center tw-gap-2">
						{/* Gradient preview circle */}
						<Tooltip placement="top">
							<Tooltip.Trigger asChild>
								<div className="tw-relative tw-size-8 tw-shrink-0 tw-overflow-hidden tw-rounded-[9999px] tw-border tw-border-solid tw-border-blue-base/15">
									<div
										className="tw-absolute tw-inset-0"
										style={CHECKER_SWATCH_STYLE}
									/>
									<div
										className="tw-absolute tw-inset-0"
										style={{ backgroundImage: _css }}
									/>
								</div>
							</Tooltip.Trigger>
							<Tooltip.Content className="tw-z-[999999]">
								<Tooltip.Text>Gradient preview</Tooltip.Text>
							</Tooltip.Content>
						</Tooltip>
						<StopsTrack
							state={state}
							dispatch={dispatch}
							canAddStop={canAddStop}
							canRemoveStop={canRemoveStop}
						/>
					</div>

					{/* Stops list */}
					<div className="tw-flex tw-flex-col tw-gap-2">
						<div className="tw-flex tw-items-center tw-justify-between">
							<span className="tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-[#12033E]">
								Stops
							</span>
							{canAddStop && (
								<button
									type="button"
									aria-label="Add stop"
									onClick={() => dispatch({ kind: "addStop", position: 50 })}
									className="tw-flex tw-size-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-text-[#12033E] tw-transition-colors hover:tw-bg-[#F3F2F5]"
								>
									<IconPlus className="tw-size-4" />
								</button>
							)}
						</div>

						<DndContext
							onDragEnd={handleDragEnd}
							sensors={sensors}
							collisionDetection={closestCenter}
							modifiers={[restrictToVerticalAxis]}
						>
							<SortableContext
								items={sortedStops.map((s) => s.id)}
								strategy={verticalListSortingStrategy}
							>
								<div className="tw-flex tw-flex-col tw-gap-2">
									{sortedStops.map((stop) => (
										<SortableStopRow
											key={stop.id}
											stop={stop}
											canRemove={canRemoveStop}
											onSelect={() => {
												dispatch({ kind: "selectStop", id: stop.id });
												setEditingStopId(stop.id);
											}}
											onRemove={() =>
												dispatch({ kind: "removeStop", id: stop.id })
											}
											onOpacityChange={(opacity) =>
												dispatch({
													kind: "updateStopOpacity",
													id: stop.id,
													opacity,
												})
											}
										/>
									))}
								</div>
							</SortableContext>
						</DndContext>
					</div>

					{/* Add gradient / Go back buttons */}
					{(onAddGradient || onGoBack) && (
						<div className="tw-flex tw-flex-col tw-gap-2">
							{onAddGradient && (
								<button
									type="button"
									onClick={onAddGradient}
									className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-primary-base tw-font-sans tw-text-sm tw-font-medium tw-text-white tw-shadow-sm tw-transition-colors hover:tw-opacity-90"
								>
									{isEditing ? "Save changes" : "Add gradient"}
								</button>
							)}
							{onGoBack && (
								<button
									type="button"
									onClick={onGoBack}
									className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-font-sans tw-text-sm tw-font-medium tw-text-[#12033E] tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 tw-transition-colors hover:tw-bg-blue-50"
								>
									Go back
								</button>
							)}
						</div>
					)}

					{/* Accessibility live region */}
					<div
						aria-live="polite"
						aria-atomic="true"
						data-testid="gradient-editor-live-region"
						className="tw-sr-only"
					>
						{announcement}
					</div>
				</div>
			)}
		</div>
	);
};

/* ── Opacity Input ── */

const OpacityInput = ({
	value,
	onChange,
}: {
	value: number;
	onChange: (opacity: number) => void;
}) => {
	const [localValue, setLocalValue] = useState(
		Math.round(value * 100).toString(),
	);
	const [isFocused, setIsFocused] = useState(false);

	// Sync from external changes when not focused
	useEffect(() => {
		if (!isFocused) {
			setLocalValue(Math.round(value * 100).toString());
		}
	}, [value, isFocused]);

	const commit = (raw: string) => {
		const parsed = Number.parseInt(raw, 10);
		if (Number.isNaN(parsed)) {
			setLocalValue(Math.round(value * 100).toString());
			return;
		}
		const clamped = Math.max(0, Math.min(100, parsed));
		onChange(clamped / 100);
		setLocalValue(clamped.toString());
	};

	return (
		<Tooltip placement="top">
			<Tooltip.Trigger asChild>
				<div className="tw-relative tw-w-[56px] tw-shrink-0">
					<input
						type="text"
						inputMode="numeric"
						aria-label="Color opacity"
						value={isFocused ? localValue : `${Math.round(value * 100)}`}
						onFocus={(e) => {
							setIsFocused(true);
							e.target.select();
						}}
						onBlur={() => {
							setIsFocused(false);
							commit(localValue);
						}}
						onChange={(e) =>
							setLocalValue(e.target.value.replace(/[^0-9]/g, ""))
						}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								commit(localValue);
								(e.target as HTMLInputElement).blur();
							} else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
								e.preventDefault();
								const current = Math.round(value * 100);
								const step = e.shiftKey ? 10 : 1;
								const next =
									e.key === "ArrowUp" ? current + step : current - step;
								const clamped = Math.max(0, Math.min(100, next));
								onChange(clamped / 100);
								setLocalValue(clamped.toString());
							}
						}}
						className="tw-h-9 tw-w-full tw-rounded-lg tw-border-none tw-bg-white tw-pr-5 tw-text-right tw-font-sans tw-text-xs tw-font-normal tw-text-[#12033E] tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-[#e7e6ec] focus:tw-outline-primary-300"
					/>
					<span className="tw-pointer-events-none tw-absolute tw-right-1.5 tw-top-1/2 -tw-translate-y-1/2 tw-font-sans tw-text-xs tw-text-blue-500">
						%
					</span>
				</div>
			</Tooltip.Trigger>
			<Tooltip.Content className="tw-z-[999999]">
				<Tooltip.Text>Color opacity</Tooltip.Text>
			</Tooltip.Content>
		</Tooltip>
	);
};

/* ── Sortable Stop Row ── */

const SortableStopRow = ({
	stop,
	canRemove,
	onSelect,
	onRemove,
	onOpacityChange,
}: {
	stop: { id: string; color: string; opacity: number };
	canRemove: boolean;
	onSelect: () => void;
	onRemove: () => void;
	onOpacityChange: (opacity: number) => void;
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: stop.id,
		transition: { duration: 200, easing: "cubic-bezier(0.25, 1, 0.5, 1)" },
	});

	const style: CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
	};

	const resolvedColor = resolveCssVar(stop.color);
	const displayHex = isCssVar(stop.color)
		? resolvedColor.toUpperCase()
		: stop.color.toUpperCase();

	const { r, g, b } = hexToRgba(resolvedColor);
	const swatchBackground = `rgba(${r}, ${g}, ${b}, ${stop.opacity})`;

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="tw-group tw-relative tw-flex tw-items-center tw-gap-2"
		>
			{/* Drag handle — visible on hover, positioned in the padding area */}
			<div
				{...listeners}
				className={cn(
					"tw-absolute -tw-left-[15px] tw-top-1/2 -tw-translate-y-1/2 tw-cursor-grab tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-50",
					isDragging && "tw-cursor-grabbing tw-opacity-50",
				)}
			>
				<IconGripVertical className="tw-size-3.5 tw-text-blue-500" />
			</div>

			{/* Stop row container */}
			<button
				type="button"
				onClick={onSelect}
				className="tw-flex tw-h-9 tw-flex-1 tw-cursor-pointer tw-items-center tw-gap-2 tw-overflow-hidden tw-rounded-lg tw-border-none tw-bg-white tw-px-1.5 tw-py-1.5 tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-[#e7e6ec]"
			>
				{/* Color swatch with checkerboard underlay for transparency */}
				<div className="tw-relative tw-size-6 tw-shrink-0 tw-overflow-hidden tw-rounded-sm tw-border tw-border-solid tw-border-blue-base/15">
					<div
						className="tw-absolute tw-inset-0"
						style={CHECKER_SWATCH_STYLE}
					/>
					<div
						className="tw-absolute tw-inset-0"
						style={{ backgroundColor: swatchBackground }}
					/>
				</div>
				{/* Hex label */}
				<span className="tw-font-sans tw-text-xs tw-font-normal tw-leading-4 tw-text-[#12033E]">
					{displayHex}
				</span>
			</button>

			{/* Opacity input */}
			<OpacityInput value={stop.opacity} onChange={onOpacityChange} />

			{/* Remove button */}
			{canRemove && (
				<button
					type="button"
					aria-label={`Remove stop ${displayHex}`}
					onClick={onRemove}
					className="tw-flex tw-size-9 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-text-[#12033E] tw-transition-colors hover:tw-bg-[#F3F2F5]"
				>
					<IconMinus className="tw-size-4" />
				</button>
			)}
		</div>
	);
};

/* ── Stop Color Editor (shown when editing a single stop's color) ── */

const StopColorEditor = ({
	stop,
	dispatch,
	themeColors,
	customSolidColors,
	onGoBack,
}: {
	stop: { id: string; color: string; opacity: number };
	dispatch: Dispatch<GradientStateAction>;
	themeColors: { key: string; tooltip: string }[];
	customSolidColors: string[];
	onGoBack: () => void;
}) => {
	// Hex input state — show resolved value if it's a CSS variable
	const [hexInput, setHexInput] = useState(resolveCssVar(stop.color));
	const [isInputFocused, setIsInputFocused] = useState(false);

	// Eyedropper state
	const [isEyeDropperSupported, setIsEyeDropperSupported] = useState(false);
	const [isEyeDropperActive, setIsEyeDropperActive] = useState(false);

	useLayoutEffect(() => {
		setIsEyeDropperSupported("EyeDropper" in window);
	}, []);

	// Sync hex input when stop color changes externally.
	useEffect(() => {
		if (!isInputFocused) {
			setHexInput(resolveCssVar(stop.color));
		}
	}, [stop.color, isInputFocused]);

	// Color picker change (from react-colorful SV + hue + alpha)
	const handlePickerChange = useCallback(
		(next: RgbaColor) => {
			const hex = rgbaToHex({ ...next, a: 1 }, false);
			if (hex !== stop.color) {
				dispatch({
					kind: "updateStopColor",
					id: stop.id,
					color: hex,
				});
			}
			if (next.a !== stop.opacity) {
				dispatch({
					kind: "updateStopOpacity",
					id: stop.id,
					opacity: next.a,
				});
			}
		},
		[dispatch, stop.id, stop.color, stop.opacity],
	);

	// Resolve CSS variable references for display in the color picker UI.
	const resolvedColor = resolveCssVar(stop.color);
	const rgbaColor: RgbaColor = {
		...hexToRgba(resolvedColor),
		a: stop.opacity,
	};

	// Hex input commit
	const commitHex = useCallback(
		(value: string) => {
			if (!HEX_RE.test(value)) {
				setHexInput(stop.color);
				return;
			}
			const normalized = normalizeHexColor(value).substring(0, 7);
			if (normalized !== stop.color) {
				dispatch({
					kind: "updateStopColor",
					id: stop.id,
					color: normalized,
				});
			}
			setHexInput(normalized);
		},
		[dispatch, stop.id, stop.color],
	);

	const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setHexInput(e.target.value);
		commitHex(e.target.value);
	};

	const handleHexKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			commitHex(hexInput);
		}
	};

	// Eyedropper
	const handleEyeDropper = useCallback(async () => {
		if (!isEyeDropperSupported) return;
		try {
			setIsEyeDropperActive(true);
			const eyeDropper = new EyeDropper();
			const result = await eyeDropper.open();
			const hex = result.sRGBHex.toUpperCase();
			dispatch({
				kind: "updateStopColor",
				id: stop.id,
				color: hex,
			});
		} catch {
			// User cancelled
		}
		setIsEyeDropperActive(false);
	}, [isEyeDropperSupported, dispatch, stop.id]);

	// Handle theme color click
	const handleThemeColor = useCallback(
		(key: string) => {
			dispatch({
				kind: "updateStopColor",
				id: stop.id,
				color: key,
			});
		},
		[dispatch, stop.id],
	);

	// Handle custom color click
	const handleCustomColor = useCallback(
		(color: string) => {
			dispatch({
				kind: "updateStopColor",
				id: stop.id,
				color,
			});
		},
		[dispatch, stop.id],
	);

	return (
		<div className="tw-flex tw-flex-col tw-gap-4">
			{/* Color picker for selected stop */}
			<div className="tw-relative" id="color-picker">
				<MemoizedColorPicker color={rgbaColor} onChange={handlePickerChange} />
				<span className="tw-absolute -tw-bottom-1 tw-left-0 tw-size-9 tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-blue-100 tw-bg-white">
					<span
						className="tw-absolute tw-inset-0"
						style={CHECKER_SWATCH_STYLE}
					/>
					<span
						className="tw-absolute tw-inset-0"
						style={{
							backgroundColor: `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${rgbaColor.a})`,
						}}
					/>
				</span>
			</div>

			{/* Hex input + eyedropper */}
			<div className="tw-flex tw-items-center [&>div:first-child]:tw-flex-grow">
				<Input
					aria-label="Hex color"
					data-testid="per-stop-hex-input"
					value={hexInput}
					placeholder="#FFFFFF"
					className="tw-h-9 tw-px-3 tw-py-1 tw-uppercase"
					onFocus={() => setIsInputFocused(true)}
					onBlur={() => {
						setIsInputFocused(false);
						commitHex(hexInput);
					}}
					onChange={handleHexChange}
					onKeyDown={handleHexKeyDown}
				/>
				{isEyeDropperSupported && (
					<button
						type="button"
						aria-label="Pick color from screen"
						onClick={handleEyeDropper}
						className={cn(
							"tw-ml-2 tw-flex tw-size-9 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 hover:tw-bg-blue-50",
							isEyeDropperActive && "tw-bg-blue-200 tw-outline-blue-300",
						)}
					>
						<IconColorPicker className="tw-size-5" />
					</button>
				)}
			</div>

			{/* Color swatches (theme + custom in one row) */}
			<div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-2">
				{themeColors.map(({ key, tooltip }) => (
					<Tooltip key={key} placement="top">
						<Tooltip.Trigger asChild>
							<button
								type="button"
								aria-label={tooltip}
								onClick={() => handleThemeColor(key)}
								className={cn(
									"tw-size-6 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-[rgba(18,3,62,0.1)] tw-transition-transform hover:tw-scale-110",
									stop.color === key &&
										"tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-primary-700",
								)}
								style={{ background: key }}
							/>
						</Tooltip.Trigger>
						<Tooltip.Content className="tw-z-[999999]">
							<Tooltip.Text>{tooltip}</Tooltip.Text>
						</Tooltip.Content>
					</Tooltip>
				))}
				{customSolidColors.map((color) => (
					<button
						key={color}
						type="button"
						aria-label={`Custom color ${color}`}
						onClick={() => handleCustomColor(color)}
						className={cn(
							"tw-size-6 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-[rgba(18,3,62,0.1)] tw-transition-transform hover:tw-scale-110",
							stop.color === color &&
								"tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-primary-700",
						)}
						style={{ backgroundColor: color }}
					/>
				))}
			</div>

			{/* Use color / Go back buttons */}
			<div className="tw-flex tw-flex-col tw-gap-2">
				<button
					type="button"
					onClick={onGoBack}
					className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-primary-base tw-font-sans tw-text-sm tw-font-medium tw-text-white tw-shadow-sm tw-transition-colors hover:tw-opacity-90"
				>
					Use color
				</button>
				<button
					type="button"
					onClick={onGoBack}
					className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-font-sans tw-text-sm tw-font-medium tw-text-[#12033E] tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 tw-transition-colors hover:tw-bg-blue-50"
				>
					Go back
				</button>
			</div>
		</div>
	);
};
